import {
    DeltaType,
    MetricConfig,
    MetricType,
    PeriodResolution,
} from "@/features/Metrics/Metrics.types";
import {
    getDeltasByCodeAtom,
    metricsAtom,
} from "@/features/Metrics/state/Metrics.state";
import { useAtomValue, useSetAtom } from "jotai";
import { DeltaIndicator } from "../../DeltaIndicator";
import {
    AIMContextViewAtom,
    AIMViewType,
} from "../../../panels/AIMPanelContext";
import clsx from "clsx";
import { EmptyMetricMessageType } from "../../../panels/EmptyMetricPanel";
import { useCallback } from "react";

export function DeltaValue({
    config,
    deltaType,
}: {
    config: MetricConfig;
    deltaType: DeltaType;
}) {
    const { isLoading } = useAtomValue(metricsAtom);
    const setContextView = useSetAtom(AIMContextViewAtom);
    const getDeltasByCode = useAtomValue(getDeltasByCodeAtom);
    const deltaCalculator = getDeltasByCode(
        [config.code],
        PeriodResolution.MONTHLY,
    )?.get(config.code);
    const openEmptyPanel = useCallback(
        (messageType: EmptyMetricMessageType) => {
            setContextView({
                type: AIMViewType.EMPTY_METRIC,
                props: {
                    config,
                    messageType:
                        config.type === MetricType.PERCENTAGE
                            ? EmptyMetricMessageType.LINEAGE_NOT_SUPPORTED
                            : messageType,
                    panelTitle: `${config.label} - % Change`,
                },
            });
        },
        [config, setContextView],
    );

    if (isLoading) {
        return (
            <div className="flex h-full w-1/2 items-center">
                <div
                    className={clsx(
                        "h-5 w-full bg-slate-700",
                        "animate-pulse rounded",
                    )}
                />
            </div>
        );
    }

    if (!deltaCalculator)
        return (
            <div
                className={clsx(
                    "w-1/3",
                    "text-center",
                    "min-w-20 cursor-pointer",
                )}
                onClick={() => openEmptyPanel(EmptyMetricMessageType.NO_DATA)}
            >
                -
            </div>
        );

    const delta = deltaCalculator.getWindowDelta(
        deltaType === DeltaType.PERIOD_OVER_PERIOD ? 1 : 12,
    );

    return (
        <div
            className={clsx(
                "w-1/3",
                "flex min-w-20 cursor-pointer justify-end",
            )}
        >
            <DeltaIndicator
                delta={
                    deltaCalculator.metric.type === MetricType.PERCENTAGE
                        ? null
                        : delta.result.value.getValue()
                }
                onClick={() => {
                    deltaCalculator.metric.type === MetricType.PERCENTAGE
                        ? openEmptyPanel(
                              EmptyMetricMessageType.LINEAGE_NOT_SUPPORTED,
                          )
                        : setContextView({
                              type: AIMViewType.DELTA_LINEAGE,
                              props: {
                                  delta: {
                                      formula: delta.getDeltaFormula(),
                                      result: delta.result.value,
                                      errorMessage: delta.getErrorMessage(),
                                      metric: deltaCalculator.metric,
                                  },
                              },
                          });
                }}
            />
        </div>
    );
}
